import {
    config,
    useEffect, useRef, useState,
    FormType
} from '../utils/globalImports';
import * as signalR from "@microsoft/signalr";

const baseURL = config[process.env.NODE_ENV as keyof typeof config].apiUrl;

export function usePageLock(formId: number, objectId: number, lockUserName: string) {
    const connectionRef = useRef<signalR.HubConnection | null>(null);
    const connectionIdRef = useRef<string | null>(null);

    const [connected, setConnected] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [lockedBy, setLockedBy] = useState<string | null>(null);

    // to bridge requestLock() with async SignalR events
    const pendingResolveRef = useRef<((res: { success: boolean; message: string; lockedBy?: string }) => void) | null>(null);

    useEffect(() => {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(`${baseURL}/formhub`)
            .withAutomaticReconnect()
            .build();

        connectionRef.current = connection;

        connection
            .start()
            .then(() => {
                setConnected(true);
                connectionIdRef.current = connection.connectionId ?? null;

                // Form successfully locked
                connection.on("LockSuccess", (formId, objectId, lockedByUser) => {
                    setIsLocked(true);
                    setLockedBy(lockedByUser);

                    if (pendingResolveRef.current) {
                        pendingResolveRef.current({
                            success: true,
                            message: "Form locked successfully",
                            lockedBy: lockedByUser,
                        });
                        pendingResolveRef.current = null;
                    }
                });

                // Lock failed (already locked by someone else)
                connection.on("LockFailed", (formId, objectId, lockedByUser) => {
                    setIsLocked(true); // still locked, just not by me
                    setLockedBy(lockedByUser);

                    const obejectName =
                        formId === FormType.LiveDefinite
                            ? "Live Definite"
                            : formId === FormType.LiveProposed
                                ? "Live Proposed"
                                : formId === FormType.ThirdPartyCost
                                ? "Third Party Cost"
                                : "";

                    if (pendingResolveRef.current) {
                        pendingResolveRef.current({
                            success: false,
                            message: `${obejectName} is being used by ${lockedByUser}`,
                            lockedBy: lockedByUser,
                        });
                        pendingResolveRef.current = null;
                    }
                });
            })
            .catch(err => console.error("SignalR Connection Error:", err));

        return () => {
            if (connectionIdRef.current) {
                requestUnlock(formId, objectId);
            }
            connection.stop();
            setConnected(false);
        };
    }, [formId, objectId, lockUserName]);

    // Lock request via SignalR
    const requestLock = (targetFormId: number, targetObjectId: number, userName: string) => {
        return new Promise<{ success: boolean; message: string; lockedBy?: string }>(async (resolve) => {
            pendingResolveRef.current = resolve;
            try {
                await connectionRef.current?.invoke("LockForm", targetFormId, targetObjectId, userName);
            } catch (err) {
                console.error("SignalR invoke failed:", err);
                resolve({ success: false, message: "Connection error while locking" });
                pendingResolveRef.current = null;
            }
        });
    };

    // Unlock request via SignalR
    const requestUnlock = async (targetFormId: number, targetObjectId: number) => {
        try {
            await connectionRef.current?.invoke("UnlockForm", targetFormId, targetObjectId);
            return { success: true, message: "Unlocked successfully" };
        } catch (err: any) {
            console.error("Unlock failed:", err);
            return { success: false, message: "Failed to unlock" };
        }
    };

    return {
        connected,
        isLocked,
        lockedBy,
        requestLock,
        requestUnlock,
    };
}
